import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosResponseHeaders } from 'axios'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { TestStatusEnum } from '@model/ClientTestAnswerDto'
import { ClientTestSubcategoryEnum } from '@model/ClientTestSubcategoryEnum'
import { CreateClientDocuments } from '@model/CreateClientDocuments'
import { CreateClientTest } from '@model/CreateClientTest'
import { DocumentPropertyType } from '@model/DocumentPropertyType'
import { UploadDocumentCategoryEnum } from '@model/UploadDocumentCategoryEnum'
import { UploadDocumentTypeEnum } from '@model/UploadDocumentTypeEnum'

import { Loading } from '@shared/components/Loading/Loading'
import { AlreadyUploadedModal } from '@shared/modals/AlreadyUploaded.modal'
import { InformationModal } from '@shared/modals/Information.modal'

import { DeclinedClassificationTest } from '../DeclinedClassificationTest'
import { PendingClassificationTest } from '../PendingClassificationTest'
import {
  ExperiencedClassificationTestForm,
  ExperiencedClassificationTestValues,
} from './ExperiencedClassificationTestForm'

interface Props {
  handleOkOnClick: () => void
}

export const ExperiencedClassificationTest: React.FC<Props> = ({ handleOkOnClick }) => {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const alreadyUploadedModal = useModal(AlreadyUploadedModal)
  const { t } = useTranslation()

  const applicationInfoModal = useModal(InformationModal, {
    title: t('Profile.Application received'),
    description: () => t('Profile.We have received your application to change classification.'),
  })
  const locale = useSessionLanguage()

  const testSubCategoryId = ClientTestSubcategoryEnum.ClassificationExperiencedRetail

  const { data: testAnswers, isLoading: isTestAnswersLoading } = useQuery({
    queryKey: queryKeys.profile.classificationTestAnswers(testSubCategoryId, locale),
    queryFn: () =>
      apiClient.getClientClassificationTestAnswers({
        subCategoryId: testSubCategoryId,
        languageId: locale,
      }),
  })

  const { data: testData, isLoading: isTestLoading } = useQuery({
    queryKey: queryKeys.profile.classificationTest(testSubCategoryId, locale),
    queryFn: () =>
      apiClient.getClassificationTest({
        subCategoryId: testSubCategoryId,
        languageId: locale,
      }),
  })

  const { data: isFormEnabled, isLoading: isTestAvailabilityLoading } = useQuery({
    queryKey: queryKeys.profile.classificationTestRetake(testSubCategoryId),
    queryFn: () => apiClient.isRetakeClassificationTestEnabled(testSubCategoryId),
  })

  const { account } = useAccountReadContext()

  const preparePayload = (values: ExperiencedClassificationTestValues): CreateClientTest => {
    return {
      category: 'classification',
      clientId: account?.id || '',
      testId: testData?.id || '',
      dateTaken: new Date().toISOString(),
      selectedAnswers: values.selectedAnswers.map((a) => a.answer),
      freeAnswers: values.freeAnswers,
    }
  }
  const getTestId = (
    headers:
      | AxiosResponseHeaders
      | Partial<
          Record<string, string> & {
            'set-cookie'?: string[] | undefined
          }
        >
  ) => {
    const parts = headers.location?.split('/')
    if (parts) {
      return parts[parts.length - 1]
    }
    return ''
  }

  const prepareUploadPayload = (
    values: ExperiencedClassificationTestValues,
    testId: string
  ): CreateClientDocuments => {
    const docs: CreateClientDocuments = {
      documents: [],
    }

    values.tradeSizeProofs.map((proof) =>
      docs.documents.push({
        categoryId: UploadDocumentCategoryEnum.Additional,
        typeId: UploadDocumentTypeEnum.Client_Classification,
        file: proof.base64Content,
        filename: proof.fileName,
        properties: {
          [DocumentPropertyType.ClientTestId]: testId,
        },
      })
    )

    values.knowledgeProofs.map((proof) =>
      docs.documents.push({
        categoryId: UploadDocumentCategoryEnum.Additional,
        typeId: UploadDocumentTypeEnum.Client_Classification,
        file: proof.base64Content,
        filename: proof.fileName,
        properties: {
          [DocumentPropertyType.ClientTestId]: testId,
        },
      })
    )

    return docs
  }

  const uploadDocs = async (values: ExperiencedClassificationTestValues, testId: string) => {
    const payload = prepareUploadPayload(values, testId)
    try {
      await apiClient.uploadDocuments(payload)
    } catch {
      alreadyUploadedModal.open()
    }
  }

  const submitForm = async (values: ExperiencedClassificationTestValues) => {
    try {
      const payload = preparePayload(values)
      if (payload) {
        const response = await apiClient.addClientClassificationTest(payload)
        await queryClient.invalidateQueries({ queryKey: ['profile'] })
        const testId = getTestId(response.headers)
        await uploadDocs(values, testId)
        applicationInfoModal.open({
          onSecondaryClick: () => {
            applicationInfoModal.close()
            handleOkOnClick()
          },
        })
      }
    } catch (error: unknown) {
      console.error(error)
    }
  }

  if (testAnswers?.testStatus.id === TestStatusEnum.Pending) {
    return <PendingClassificationTest />
  } else if (testAnswers?.testStatus.id === TestStatusEnum.Declined && !testAnswers.allowRetake) {
    return <DeclinedClassificationTest handleOkOnClick={handleOkOnClick} />
  }

  return (
    <Loading isLoading={isTestLoading || isTestAnswersLoading || isTestAvailabilityLoading}>
      {testData && (
        <ExperiencedClassificationTestForm
          classificationName={account?.classification.name}
          testData={testData}
          handleFormSubmit={submitForm}
          isFormEnabled={!!isFormEnabled}
          testAnswers={testAnswers}
          account={account}
        />
      )}
    </Loading>
  )
}
